import {
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {HStack, Text, VStack} from 'platform/foundation';

import {useId} from 'react';

import {
  HyundaiAs1qImportOptions,
  useGetHyundaiAs1qUserOptionsQuery,
  usePostHyundaiAs1qRetrieveMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {TestIdProps, suffixTestId} from 'shared/web';

import {refreshServiceOrderGrids} from '../../../utils/refreshServiceOrderGrids';

const DEFAULT_OPTIONS: HyundaiAs1qImportOptions = {
  material: true,
  labourSummarized: false,
  labourItemized: true,
  sundries: true,
  sublets: true,
  accessories: true,
};

const SETTINGS: {key: keyof HyundaiAs1qImportOptions; label: string}[] = [
  {key: 'material', label: i18n.t('entity.calculationType.labels.material')},
  {key: 'labourSummarized', label: i18n.t('entity.calculationType.labels.labourSummarized')},
  {key: 'labourItemized', label: i18n.t('entity.calculationType.labels.labourItemized')},
  {key: 'sundries', label: i18n.t('entity.calculationType.labels.sundries')},
  {key: 'sublets', label: i18n.t('entity.calculationType.labels.sublets')},
  {key: 'accessories', label: i18n.t('entity.calculationType.labels.accessories')},
];

interface HyundaiAs1qRetrieveDialogProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  onClose: () => void;
}

export function HyundaiAs1qRetrieveDialog(props: HyundaiAs1qRetrieveDialogProps) {
  // Switch labels are rendered outside Switch to keep the design's bold text, so they
  // need an explicit id to stay associated with their input.
  const labelIdPrefix = useId();
  const {
    data: storedOptions,
    isLoading: isLoadingStoredOptions,
    isError: isStoredOptionsError,
  } = useGetHyundaiAs1qUserOptionsQuery();

  const [postHyundaiAs1qRetrieve] = usePostHyundaiAs1qRetrieveMutation();

  const handleSubmit: FormSubmitHandler<HyundaiAs1qImportOptions> = (options) =>
    postHyundaiAs1qRetrieve({
      body: {
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
        options,
      },
    })
      .unwrap()
      .then(() => {
        refreshServiceOrderGrids();
        props.onClose();
      })
      .catch(handleApiError);

  return (
    <DataStatus isLoading={isLoadingStoredOptions} isError={isStoredOptionsError}>
      <Form<HyundaiAs1qImportOptions>
        onSubmit={handleSubmit}
        defaultValues={storedOptions ?? DEFAULT_OPTIONS}
      >
        {(control) => (
          <>
            <VStack spacing={4}>
              <Text color="secondary" size="small">
                {i18n.t('entity.calculationType.labels.importWorkItemsInfo')}
              </Text>
              <VStack spacing={2}>
                {SETTINGS.map((setting) => (
                  <Card key={setting.key} variant="inlineGrey">
                    <HStack align="center" spacing={3}>
                      <FormField
                        name={setting.key}
                        type="switch"
                        control={control}
                        externalLabelId={`${labelIdPrefix}${setting.key}`}
                        data-testid={suffixTestId(setting.key, props)}
                      />
                      <label htmlFor={`${labelIdPrefix}${setting.key}`}>
                        <Text alternative>{setting.label}</Text>
                      </label>
                    </HStack>
                  </Card>
                ))}
              </VStack>
            </VStack>
            <DialogFooter>
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={props.onClose}
                  data-testid={suffixTestId('discard', props)}
                />
                <FormButton
                  control={control}
                  title={i18n.t('general.actions.confirm')}
                  variant="primary"
                  type="submit"
                  data-testid={suffixTestId('confirm', props)}
                />
              </ButtonGroup>
            </DialogFooter>
          </>
        )}
      </Form>
    </DataStatus>
  );
}
